import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { basicsSchema } from "@reactive-resume/schema";
import { Cascader, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@reactive-resume/ui";
import { useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

import { CustomFieldsSection } from "./custom/section";
import { PictureSection } from "./picture/section";
import { SectionIcon } from "./shared/section-icon";
import { URLInput } from "./shared/url-input";
import { chinaCascaderData, getCascaderDisplayText } from "../../../../../data/china-cascader-data";

export const BasicsSection = () => {
  const { i18n } = useLingui();
  const setValue = useResumeStore((state) => state.setValue);
  const basics = useResumeStore((state) => state.resume.data.basics);

  // 根据语言设置确定性别选项
  const isChinese = i18n.locale.startsWith("zh");
  const genderOptions = isChinese 
    ? [
        { value: "男", label: "男" },
        { value: "女", label: "女" },
        { value: "其他", label: "其他" }
      ]
    : [
        { value: "Male", label: "Male" },
        { value: "Female", label: "Female" },
        { value: "Other", label: "Other" }
      ];

  const genderPlaceholder = isChinese ? "请选择性别" : "Select gender";

  // 处理级联选择器的变化
  const handleCascaderChange = (values: string[]) => {
    const displayText = getCascaderDisplayText(values);
    setValue("basics.location", displayText);
  };

  // 从当前 location 值解析出级联选择器的值
  const getCascaderValue = (): string[] => {
    if (!basics.location || !isChinese) return [];
    
    // 简单的解析逻辑，可以根据需要优化
    const parts = basics.location.split(" ");
    if (parts.length >= 2) {
      // 尝试找到匹配的省市代码
      for (const province of chinaCascaderData) {
        if (province.label === parts[0]) {
          for (const city of province.children || []) {
            if (city.label === parts[1]) {
              return [province.value, city.value];
            }
          }
          return [province.value];
        }
      }
    } else if (parts.length === 1) {
      // 只有省份
      for (const province of chinaCascaderData) {
        if (province.label === parts[0]) {
          return [province.value];
        }
      }
    }
    return [];
  };

  return (
    <section id="basics" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="basics" size={18} />
          <h2 className="line-clamp-1 text-2xl font-bold lg:text-3xl">{t`Basics`}</h2>
        </div>
      </header>

      <main className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <PictureSection />
        </div>

        <div className="space-y-4 sm:col-span-2">
          <Label htmlFor="basics.name">{t`Full Name`}</Label>
          <Input
            id="basics.name"
            value={basics.name}
            hasError={!basicsSchema.pick({ name: true }).safeParse({ name: basics.name }).success}
            onChange={(event) => {
              setValue("basics.name", event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="basics.headline">{t`Headline`}</Label>
          <Input
            id="basics.headline"
            value={basics.headline}
            onChange={(event) => {
              setValue("basics.headline", event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="basics.email">{t`Email`}</Label>
          <Input
            id="basics.email"
            placeholder="john.doe@example.com"
            value={basics.email}
            hasError={
              !basicsSchema.pick({ email: true }).safeParse({ email: basics.email }).success
            }
            onChange={(event) => {
              setValue("basics.email", event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="basics.url">{t`Website`}</Label>
          <URLInput
            id="basics.url"
            value={basics.url}
            placeholder="https://example.com"
            onChange={(value) => {
              setValue("basics.url", value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="basics.phone">{t`Phone`}</Label>
          <Input
            id="basics.phone"
            placeholder="+1 (123) 4567 7890"
            value={basics.phone}
            onChange={(event) => {
              setValue("basics.phone", event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="basics.location">{t`Location`}</Label>
          {isChinese ? (
            <Cascader
              options={chinaCascaderData}
              value={getCascaderValue()}
              onChange={handleCascaderChange}
              placeholder="请选择省份/城市"
            />
          ) : (
            <Input
              id="basics.location"
              value={basics.location}
              onChange={(event) => {
                setValue("basics.location", event.target.value);
              }}
            />
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="basics.gender">{t`Gender`}</Label>
          <Select
            value={basics.gender}
            onValueChange={(value) => {
              setValue("basics.gender", value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={genderPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {genderOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <CustomFieldsSection className="sm:col-span-2" />
      </main>
    </section>
  );
};
